import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    HostListener,
    inject,
    input,
    signal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { Workflow } from "../service/Workflow";
import WorkflowItem = Workflow.WorkflowItem;
import { s25Resource } from "../../../util/s25.resource";
import { ContactService } from "../../../services/contact.service";
import { WorkflowChatType } from "../../../services/soc.chat.channel.util";
import { S25Util } from "../../../util/s25-util";
import { SeSecurityStore } from "../../student-engagement/se.security.store";
import { WorkflowChatController } from "../chat/workflow.chat.controller";
import { WorkflowChatPanelComponent } from "../chat/workflow.chat.panel.component";

@TypeManagerDecorator("my-workflow-chat-dropdown")
@Component({
    selector: "my-workflow-chat-dropdown",
    template: `
        @if (securityStore.canAccessChat()) {
            <div class="chat-dropdown">
                <button
                    #trigger
                    class="chat-trigger"
                    (click)="toggleChatMenu(trigger)"
                    [class.active]="chatMenuOpen()"
                    [attr.aria-expanded]="userIsSubmitter() ? null : chatMenuOpen()"
                >
                    <s25-ng-icon [type]="'chat'" [ariaLabel]="false" />
                    {{ dropdownText() }}
                    @if (!userIsSubmitter()) {
                        <span class="dropdown-arrow" aria-hidden="true">▾</span>
                    }
                </button>
                @if (!userIsSubmitter() && chatMenuOpen()) {
                    <div class="chat-menu">
                        <button class="chat-option" (click)="startChat('submitter', trigger)">
                            <s25-ng-icon [type]="'user'" class="chat-icon" [ariaLabel]="false" />
                            Chat with Submitter
                        </button>
                        <button class="chat-option" (click)="startChat('all', trigger)">
                            <s25-ng-icon [type]="'users'" class="chat-icon" [ariaLabel]="false" />
                            Chat with Everyone
                        </button>
                        <button class="chat-option" (click)="startChat('reviewers', trigger)">
                            <s25-ng-icon [type]="'lock'" class="chat-icon" [ariaLabel]="false" />
                            Chat with Reviewers Only
                        </button>
                    </div>
                }
            </div>
            <workflow-chat-panel [controller]="chat" />
        }
    `,
    host: {
        "[class.mobile]": "this.mobile",
    },
    styles: `
        :host {
            display: block;
        }

        .chat-dropdown {
            position: relative;
            height: 100%;
        }

        .chat-trigger {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            gap: 6px;
            padding: 8px 14px;
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            color: var(--color--text);
            transition: all 0.2s;
        }

        .chat-trigger:hover,
        .chat-trigger.active {
            background: var(--color--surface-2-bg);
            border-color: var(--color--border-bold);
        }

        .dropdown-arrow {
            font-size: 10px;
            transition: transform 0.2s;
        }

        .chat-trigger.active .dropdown-arrow {
            transform: rotate(180deg);
        }

        .chat-menu {
            position: absolute;
            top: calc(100% + 4px);
            right: 0;
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            box-shadow: var(--shadow--dropdown);
            min-width: 200px;
            z-index: 100;
            overflow: hidden;
        }

        .chat-option {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            padding: 12px 16px;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 14px;
            color: var(--color--text-strong);
            text-align: left;
            transition: background 0.15s;
        }

        .chat-option:hover {
            background: var(--color--surface-2-bg);
        }

        .chat-option:not(:last-child) {
            border-bottom: 1px solid var(--color--border);
        }

        .chat-icon {
            --icon-size: 16px;
            color: var(--color--text-muted);
        }

        :host.mobile {
            .chat-menu {
                position: fixed;
                top: auto;
                right: 8px;
                left: 8px;
                bottom: 8px;
                min-width: unset;
                width: auto;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent, WorkflowChatPanelComponent],
})
export class MyWorkflowChatDropdownComponent {
    submission = input.required<WorkflowItem>();

    protected readonly securityStore = inject(SeSecurityStore);
    private readonly elementRef = inject(ElementRef);

    protected readonly chat = new WorkflowChatController();
    protected chatMenuOpen = signal<boolean>(false);
    readonly mobile = S25Util.mobileCheck();

    private userIdResp = s25Resource({
        loader: () => ContactService.getCurrentId(),
    });
    protected userIsSubmitter = computed(() => this.userIdResp.value() === this.submission().contId);
    protected dropdownText = computed(() => (this.userIsSubmitter() ? "Chat with Reviewers" : "Chat"));

    @HostListener("document:click", ["$event"])
    onClick(event: MouseEvent) {
        if (this.chatMenuOpen() && !this.elementRef.nativeElement.contains(event.target)) this.chatMenuOpen.set(false);
    }

    toggleChatMenu(trigger?: HTMLElement) {
        if (this.userIsSubmitter()) {
            void this.startChat("reviewers", trigger);
            return;
        }
        this.chatMenuOpen.update((prev) => !prev);
    }

    async startChat(chatType: WorkflowChatType, trigger?: HTMLElement) {
        this.chatMenuOpen.set(false);
        await this.chat.start(this.submission(), { kind: "group", chatType }, trigger);
    }
}
